import { Box, Grid } from "@mui/material";
import { useState } from "react";
import { BookingData } from "../../../types/view-booking";
import BookingSidebar from "../../components/BookingsSidebar";
import { CoverPageTemplate } from "../../types/cover.types";
import { Proposal } from "../../types/proposal.types";
import { useProposals } from "./components/proposals-page/hooks/UseProposals";
import ProposalList from "./components/proposals-page/Proposallist";
import CreateProposalPage from "./CreateProposal";
import ViewProposalPage from "./ViewProposalPage";

interface ProposalTabProps {
  bookingData: BookingData | null;
}

export default function ProposalTab({ bookingData }: ProposalTabProps) {
  const [selectedProposalId, setSelectedProposalId] = useState<number | null>(
    null,
  );
  const [selectedCoverTemplate, setSelectedCoverTemplate] =
    useState<CoverPageTemplate | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const {
    proposals,
    loading,
    refetch,
    totalCount,
    nextUrl,
    prevUrl,
    goToNext,
    goToPrev,
    setProposals,
  } = useProposals();
  
  const defaultServices = bookingData?.package?.items?.map((item) => ({
    service: {
      id: item.id,
      name: item.name,
      price: item.price,
      description: "",
      service_category: "",
      service_category_display: "",
      customizable: false,
      features: [],
    },
    quantity: 1,
  }));

  const selectedProposal = proposals.find((p) => p.id === selectedProposalId);

  const clientName = bookingData?.organiser?.name || "";

  const deceasedName = bookingData?.beloved?.name || "";

  const email = bookingData?.organiser?.email || "";

  const phone = bookingData?.organiser?.phone || "";

  const address = bookingData?.organiser?.address || "";

  const handleStatusChange = (id: number, newStatus: string) => {
    setProposals((prev) =>
      prev.map((p) =>
        p.id === id ? { ...p, status: newStatus as Proposal["status"] } : p,
      ),
    );
  };

  if (isCreating) {
    return (
      <CreateProposalPage
        onTemplateChange={setSelectedCoverTemplate}
        onBack={() => setIsCreating(false)}
        defaultValues={{
          clientName,
          deceasedName,
          email,
          phone,
          address,
          packageName: bookingData?.package?.name,
          serviceType: bookingData?.service,
          bookingId: bookingData?.id,
          defaultServices,
        }}
        onSave={() => {
          setIsCreating(false);
          refetch();
        }}
        onSendToClient={() => {
          setIsCreating(false);
          refetch();
        }}
        onFinishLater={() => {
          setIsCreating(false);
          refetch();
        }}
      />
    );
  }

  if (isUpdating && selectedProposal) {
    return (
      <CreateProposalPage
        onTemplateChange={setSelectedCoverTemplate}
        onBack={() => setIsUpdating(false)}
        defaultValues={{
          clientName,
          deceasedName,
          email,
          phone,
          address,
          packageName: selectedProposal.package?.name,
          serviceType: bookingData?.service,
          bookingId: bookingData?.id,
          proposalId: selectedProposal.id,
          introductionLetter: selectedProposal.introduction_letter,
          termsAndConditions: selectedProposal.terms_and_conditions,
          previousStreams:
            selectedProposal.links?.map((link) => ({
              id: link.id,
              title: link.title,
              url: link.url,
            })) ?? [],
          defaultServices: selectedProposal.services?.map((s) => ({
            service: {
              id: s.id,
              name: s.name,
              price: Number(s.price),
              description: s.description ?? "",
              service_category: s.service_category,
              service_category_display: s.service_category,
              customizable: false,
              features: [],
            },
            quantity: 1,
          })),
        }}
        onSave={() => {
          setIsUpdating(false);
          refetch();
        }}
        onFinishLater={() => {
          setIsUpdating(false);
          refetch();
        }}
      />
    );
  }

  if (selectedProposal) {
    return (
      <ViewProposalPage
        selectedCoverTemplate={selectedCoverTemplate}
        proposal={selectedProposal}
        clientEmail={bookingData?.organiser?.email}
        onBack={() => setSelectedProposalId(null)}
        onUpdateProposal={() => setIsUpdating(true)}
        onGenerateInvoice={() => console.log("generate invoice")}
        onConfirmBooking={() => console.log("confirm booking")}
        status={selectedProposal.status}
        onStatusChange={handleStatusChange}
        previousStreams={selectedProposal.links ?? []}
      />
    );
  }

  return (
    <Box>
      {/* <ProposalBanner onAction={() => console.log("banner action")} /> */}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 9 }}>
          <ProposalList
            proposals={proposals}
            loading={loading}
            totalCount={totalCount}
            nextUrl={nextUrl}
            prevUrl={prevUrl}
            onNext={goToNext}
            onPrev={goToPrev}
            onCreateProposal={() => setIsCreating(true)}
            onViewProposal={(id) => setSelectedProposalId(id)}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <BookingSidebar bookingData={bookingData} />
        </Grid>
      </Grid>
    </Box>
  );
}
